import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { Init } from '@sorare/wallet-shared';
import { MessagingContext } from '@sorare/wallet-shared';
import CenteredLoadingBall from 'components/CenteredLoadingBall';
import defaultDict from 'lib/dict';

type User = Init['response']['result']['user'];
type Dict = Init['response']['result']['dict'];
type FeatureFlags = Init['response']['result']['featureFlags'];
type Info = Omit<Init['response']['result'], 'user' | 'dict' | 'featureFlags'>;

interface InfoContext {
  user: User;
  dict: Dict;
  info: Info;
  featureFlags: FeatureFlags;
  setUser: (user: User) => void;
  fetchUser: () => void;
  logOut: () => void;
}

const Context = createContext<InfoContext | null>(null);

export const InfoProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null);
  const [dict, setDict] = useState<Dict | null>(null);
  const [info, setInfo] = useState<Info | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});
  const { sendRequest } = useContext(MessagingContext)!;

  const logOut = useCallback(() => setUser(undefined), []);

  const fetchUser = useCallback(() => {
    // https://github.com/facebook/react/issues/34905
    sendRequest<Init>('init', {}).then(({ result }) => {
      setUser(result.user);
      setDict(Object.assign(defaultDict, result.dict));
      setFeatureFlags(result.featureFlags);
      setInfo(result);
    });
  }, [sendRequest]);

  useEffect(() => {
    if (user === null) fetchUser();
  }, [fetchUser, user]);

  useEffect(() => {
    if (info?.langDir) {
      const html = window.document.documentElement;

      html.dir = info.langDir;
    }
  }, [info]);

  if (user === null || dict === null || info === null)
    return <CenteredLoadingBall />;

  return (
    <Context.Provider
      value={{ dict, featureFlags, info, user, setUser, logOut, fetchUser }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
